import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { SwanLogo, TaglineDivider } from "@/components/Brand";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword({ forced }) {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) return setError("New passwords do not match");
    if (next.length < 8) return setError("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: user?.must_change_password ? undefined : current,
        new_password: next,
      });
      toast.success("Password updated");
      await refresh();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full card-surface rounded-xl px-4 py-3.5 text-white outline-none focus:border-[#C9A84A]/60 transition-colors mb-4";

  return (
    <div className="swan-bg min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-2">
          <SwanLogo size={72} />
        </div>
        <TaglineDivider />
        <form onSubmit={submit} className="mt-4" data-testid="change-password-form">
          <h2 className="font-serif font-bold text-2xl text-white">
            {forced && user?.must_change_password ? "Set your password" : "Change password"}
          </h2>
          <p className="text-slate-400 mt-1 mb-6">
            {forced && user?.must_change_password
              ? "You're signing in with a temporary password. Choose a new one to continue."
              : "Keep your account secure with a strong password."}
          </p>
          {!user?.must_change_password && (
            <input type="password" placeholder="Current password" value={current}
              onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="current-password" />
          )}
          <input type="password" placeholder="New password" value={next}
            onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="new-password" required />
          <input type="password" placeholder="Confirm new password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="confirm-password" required />
          {error && <p className="text-red-300 text-sm mb-4">{error}</p>}
          <button disabled={busy} data-testid="change-password-submit"
            className="w-full bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-xl py-3.5 transition-colors disabled:opacity-60">
            {busy ? "Saving…" : "Save password"}
          </button>
        </form>
        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mt-8">
          <Lock size={14} /> Bank-grade encryption · UK GDPR compliant
        </div>
      </div>
    </div>
  );
}
